﻿/**
 * SyncStatus Dashboard â€” Phase 7: Analytics & Monitoring
 *
 * Live dashboard showing connector sync health, provider status,
 * recent sync activity, alerts, and data quality metrics.
 */

import { useState, useEffect, useCallback, type ReactElement } from 'react'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle
} from '@retailsync/ui'
import { Button } from '@retailsync/ui'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ClockIcon,
  ChartBarIcon,
  BellAlertIcon,
  ShieldCheckIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import {
  API_ORIGIN,
  ApiError,
  getAuthHeaders,
  readApiResponse,
  describeLicenseError,
  LICENSE_ACTION_URL,
} from '../config/api'

// â”€â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface ActivitySummary {
  today: { syncs: number; records: number }
  thisWeek: { syncs: number; records: number }
  providersActive: number
  providersTotal: number
}

interface ProviderHealth {
  providerId: string
  providerName: string
  syncStatus: string
  lastSyncAt: string | null
  nextSyncDue: string | null
  syncSchedule: string | null
  successRate: number
  consecutiveFailures: number
  isStale: boolean
}

interface SyncAlert {
  id: string
  type: string
  severity: string
  message: string
  acknowledged: boolean
  createdAt: string
  providerId?: string
}

interface SyncStats {
  totalSyncs: number
  successfulSyncs: number
  failedSyncs: number
  successRate: number
  totalRecordsProcessed: number
  avgDurationMs: number
  lastSyncAt: string | null
}

interface DataQuality {
  overallScore: number
  validationPassRate: number
  anomalies: string[]
}

// â”€â”€â”€ API helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const API_BASE = API_ORIGIN
async function fetchFromApi<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}/api/v1/connector${path}`, { headers: getAuthHeaders() })

  // Surfaces the 402 license payload (code/action/actionLabel) instead of a bare
  // status code, so the page can explain WHY it is blocked.
  const json = await readApiResponse<{ success: boolean; data: T; error?: string }>(res)
  if (!json.success) throw new ApiError(json.error || 'Unknown error', res.status, json as any)
  return json.data
}

// â”€â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function SyncStatus() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'providers' | 'alerts' | 'quality'>('overview')

  const [activity, setActivity] = useState<ActivitySummary | null>(null)
  const [stats, setStats] = useState<SyncStats | null>(null)
  const [providers, setProviders] = useState<ProviderHealth[]>([])
  const [alerts, setAlerts] = useState<SyncAlert[]>([])
  const [quality, setQuality] = useState<DataQuality | null>(null)
  const [healthChecking, setHealthChecking] = useState(false)
  // Set when the API rejects the tenant with 402 — renders the license action instead
  // of a generic failure, and stops the remaining calls.
  const [licenseGate, setLicenseGate] = useState<{ message: string; actionLabel: string } | null>(null)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    setLicenseGate(null)
    try {
      // Probe once first: an unlicensed tenant would otherwise fire all six calls
      // and flood the console with identical 402s.
      const act = await fetchFromApi<ActivitySummary>('/analytics/summary')

      const [st, prov, al, q] = await Promise.all([
        fetchFromApi<SyncStats>('/analytics/stats?period=7d'),
        fetchFromApi<ProviderHealth[]>('/analytics/providers'),
        fetchFromApi<SyncAlert[]>('/alerts?acknowledged=false'),
        fetchFromApi<DataQuality>('/analytics/quality'),
      ])
      setActivity(act)
      setStats(st)
      setProviders(prov)
      setAlerts(al)
      setQuality(q)
    } catch (err: any) {
      if (err instanceof ApiError && err.licenseRequired) {
        setLicenseGate(describeLicenseError(err))
      } else {
        setError(err.message)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const runHealthCheck = async () => {
    setHealthChecking(true)
    try {
      const token = localStorage.getItem('access_token') || ''
      const tenantId = localStorage.getItem('tenant_id') || ''
      await fetch(`${API_BASE}/api/v1/connector/analytics/health-check`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
          'Content-Type': 'application/json',
        },
      })
      await loadData()
    } catch (err: any) {
      console.error('Health check failed:', err)
    } finally {
      setHealthChecking(false)
    }
  }

  const acknowledgeAlert = async (alertId: string) => {
    const token = localStorage.getItem('access_token') || ''
    const tenantId = localStorage.getItem('tenant_id') || ''
    await fetch(`${API_BASE}/api/v1/connector/alerts/${alertId}/acknowledge`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'x-tenant-id': tenantId,
      },
    })
    setAlerts(alerts.filter(a => a.id !== alertId))
  }

  // â”€â”€â”€ Render helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  function StatusBadge({ status }: { status: string }) {
    const config: Record<string, { color: string; icon: ReactElement; label: string }> = {
      healthy: { color: 'bg-green-100 text-green-800', icon: <CheckCircleIcon className="w-4 h-4" />, label: 'Healthy' },
      failing: { color: 'bg-red-100 text-red-800', icon: <XCircleIcon className="w-4 h-4" />, label: 'Failing' },
      paused: { color: 'bg-yellow-100 text-yellow-800', icon: <ClockIcon className="w-4 h-4" />, label: 'Paused' },
      unknown: { color: 'bg-gray-100 text-gray-800', icon: <ClockIcon className="w-4 h-4" />, label: 'Unknown' },
    }
    const c = config[status] || config.unknown
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.color}`}>
        {c.icon}{c.label}
      </span>
    )
  }

  function SeverityBadge({ severity }: { severity: string }) {
    const config: Record<string, string> = {
      critical: 'bg-red-600 text-white',
      warning: 'bg-amber-500 text-white',
      info: 'bg-blue-500 text-white',
    }
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${config[severity] || 'bg-gray-500 text-white'}`}>
        {severity.toUpperCase()}
      </span>
    )
  }

  function timeAgo(dateStr: string | null): string {
    if (!dateStr) return 'Never'
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'Just now'
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
  }

  // â”€â”€â”€ Loading / Error states â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  // License gate — the request never reached the connector routes.
  if (licenseGate) {
    return (
      <div className="max-w-lg mx-auto text-center py-12">
        <ExclamationTriangleIcon className="w-12 h-12 text-amber-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900">License required</h3>
        <p className="text-gray-500 mt-1">{licenseGate.message}</p>
        <a
          href={LICENSE_ACTION_URL}
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
        >
          {licenseGate.actionLabel}
        </a>
        <div>
          <Button variant="outline" onClick={loadData} className="mt-3">Retry</Button>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900">Failed to load sync data</h3>
        <p className="text-gray-500 mt-1">{error}</p>
        <Button onClick={loadData} className="mt-4">Retry</Button>
      </div>
    )
  }

  // â”€â”€â”€ Main render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sync Monitoring</h1>
          <p className="text-gray-500 mt-1">Real-time connector health &amp; analytics</p>
        </div>
        <Button
          onClick={runHealthCheck}
          disabled={healthChecking}
          className="flex items-center gap-2"
        >
          <ArrowPathIcon className={`w-4 h-4 ${healthChecking ? 'animate-spin' : ''}`} />
          {healthChecking ? 'Checking...' : 'Run Health Check'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { key: 'overview', label: 'Overview', icon: ChartBarIcon },
          { key: 'providers', label: 'Providers', icon: ShieldCheckIcon, count: providers.length },
          { key: 'alerts', label: 'Alerts', icon: BellAlertIcon, count: alerts.length },
          { key: 'quality', label: 'Data Quality', icon: CheckCircleIcon },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                tab.key === 'alerts' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* â”€â”€â”€ Overview Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{stats?.successRate ?? 0}%</div>
                <div className="text-sm text-gray-500">Success Rate</div>
                <div className="text-xs text-gray-400 mt-1">
                  {stats?.successfulSyncs ?? 0}/{stats?.totalSyncs ?? 0} syncs
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{activity?.today.syncs ?? 0}</div>
                <div className="text-sm text-gray-500">Today's Syncs</div>
                <div className="text-xs text-gray-400 mt-1">
                  {activity?.today.records ?? 0} records
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{activity?.providersActive ?? 0}/{activity?.providersTotal ?? 0}</div>
                <div className="text-sm text-gray-500">Active Providers</div>
                <div className="text-xs text-gray-400 mt-1">
                  {alerts.length} unread alerts
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{stats?.avgDurationMs ?? 0}ms</div>
                <div className="text-sm text-gray-500">Avg Sync Duration</div>
                <div className="text-xs text-gray-400 mt-1">
                  Last: {timeAgo(stats?.lastSyncAt ?? null)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Weekly Activity */}
          <Card>
            <CardHeader>
              <CardTitle>This Week</CardTitle>
              <CardDescription>Sync activity overview</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-3xl font-bold text-blue-600">{activity?.thisWeek.syncs ?? 0}</div>
                  <div className="text-sm text-gray-500">Total Sync Operations</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-green-600">{activity?.thisWeek.records ?? 0}</div>
                  <div className="text-sm text-gray-500">Records Processed</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* â”€â”€â”€ Providers Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {activeTab === 'providers' && (
        <div className="space-y-4">
          {providers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ShieldCheckIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No providers configured yet</p>
              <p className="text-sm">Add a connector provider to start monitoring</p>
            </div>
          ) : (
            providers.map(p => (
              <Card key={p.providerId} className={p.isStale ? 'border-amber-300' : ''}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{p.providerName}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <StatusBadge status={p.syncStatus} />
                          {p.isStale && (
                            <span className="text-xs text-amber-600 flex items-center gap-1">
                              <ExclamationTriangleIcon className="w-3 h-3" /> Stale
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium">
                        {p.successRate}% success
                      </div>
                      <div className="text-xs text-gray-500">
                        Last sync: {timeAgo(p.lastSyncAt)}
                      </div>
                      {p.nextSyncDue && (
                        <div className="text-xs text-gray-400">
                          Next: {timeAgo(p.nextSyncDue)} from now
                        </div>
                      )}
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                  </div>
                  {p.consecutiveFailures > 0 && (
                    <div className="mt-3 text-sm text-red-600 bg-red-50 rounded px-3 py-2">
                      {p.consecutiveFailures} consecutive failure{p.consecutiveFailures > 1 ? 's' : ''}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* â”€â”€â”€ Alerts Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {activeTab === 'alerts' && (
        <div className="space-y-3">
          {alerts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <BellAlertIcon className="w-12 h-12 mx-auto mb-3 text-green-300" />
              <p>No active alerts</p>
              <p className="text-sm">Everything looks good!</p>
            </div>
          ) : (
            alerts.map(alert => (
              <Card key={alert.id} className={
                alert.severity === 'critical' ? 'border-red-300 bg-red-50' :
                alert.severity === 'warning' ? 'border-amber-200 bg-amber-50' : ''
              }>
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {alert.severity === 'critical' ? (
                        <XCircleIcon className="w-5 h-5 text-red-500 mt-0.5" />
                      ) : alert.severity === 'warning' ? (
                        <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 mt-0.5" />
                      ) : (
                        <BellAlertIcon className="w-5 h-5 text-blue-500 mt-0.5" />
                      )}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <SeverityBadge severity={alert.severity} />
                          <span className="text-xs text-gray-500">{alert.type.replace('_', ' ')}</span>
                          <span className="text-xs text-gray-400">{timeAgo(alert.createdAt)}</span>
                        </div>
                        <p className="text-sm text-gray-900">{alert.message}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => acknowledgeAlert(alert.id)}
                    >
                      Dismiss
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* â”€â”€â”€ Data Quality Tab â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {activeTab === 'quality' && (
        <div className="space-y-6">
          {/* Quality Score */}
          <Card>
            <CardHeader>
              <CardTitle>Data Quality Score</CardTitle>
              <CardDescription>Based on validation pass rate and sync success (last 30 days)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                {/* Score circle */}
                <div className="relative w-24 h-24">
                  <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                    <circle
                      cx="50" cy="50" r="42" fill="none"
                      stroke={quality && quality.overallScore >= 80 ? '#22c55e' :
                              quality && quality.overallScore >= 60 ? '#f59e0b' : '#ef4444'}
                      strokeWidth="8"
                      strokeDasharray={`${(quality?.overallScore ?? 0) * 2.64} 264`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold">{quality?.overallScore ?? 0}</span>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Validation Pass Rate</div>
                  <div className="text-xl font-semibold">{quality?.validationPassRate ?? 0}%</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Anomalies */}
          {quality && quality.anomalies.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-500" />
                  Detected Anomalies
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {quality.anomalies.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-amber-500 mt-0.5">â€¢</span>
                      {a}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
